import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import { Loader2, AlertCircle, BarChart3, MousePointerClick, Eye, XCircle, ShoppingCart, Package, CreditCard, CheckCircle } from 'lucide-react';

interface Popup {
  id: number;
  name: string;
  trackingId?: string;
}

interface PopupAnalytics {
  popupId: string;
  impressions: number;
  clicks: number;
  closes: number;
  clickThroughRate: string;
  conversions: {
    store_visit: number;
    product_view: number;
    add_to_cart: number;
    begin_checkout: number;
    order_completed: number;
  };
  conversionRate: {
    clickToOrder: string;
    impressionToOrder: string;
  };
}

interface Props {
  popups: Popup[];
}

function getDateRange(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

function toUnixSeconds(dateStr: string): number {
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

const CONVERSION_STEPS = [
  { key: 'store_visit', label: '進入 /store', icon: MousePointerClick },
  { key: 'product_view', label: '瀏覽產品', icon: Package },
  { key: 'add_to_cart', label: '加入購物車', icon: ShoppingCart },
  { key: 'begin_checkout', label: '開始結帳', icon: CreditCard },
  { key: 'order_completed', label: '完成訂單', icon: CheckCircle },
] as const;

const PopupAnalyticsReport: React.FC<Props> = ({ popups }) => {
  const [selectedPopup, setSelectedPopup] = useState<string>('');
  const [days, setDays] = useState<number>(30);
  const [startDate, setStartDate] = useState<string>(getDateRange(30).start);
  const [endDate, setEndDate] = useState<string>(getDateRange(30).end);
  const [data, setData] = useState<PopupAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const popupOptions = useMemo(() => {
    return popups.filter((p) => p.trackingId).map((p) => ({ value: p.trackingId!, label: p.name }));
  }, [popups]);

  useEffect(() => {
    if (popupOptions.length > 0 && !selectedPopup) {
      setSelectedPopup(popupOptions[0].value);
    }
  }, [popupOptions, selectedPopup]);

  useEffect(() => {
    const { start, end } = getDateRange(days);
    setStartDate(start);
    setEndDate(end);
  }, [days]);

  useEffect(() => {
    if (!selectedPopup) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const start = toUnixSeconds(startDate);
        const end = toUnixSeconds(endDate) + 86399; // end of day
        const res = await apiFetch(
          `/api/admin/analytics/popups?popupId=${encodeURIComponent(selectedPopup)}&startDate=${start}&endDate=${end}`
        );
        if (!res.ok) throw new Error('載入報表失敗');
        const json = await res.json();
        setData(json.data);
      } catch (err: any) {
        setError(err.message || '載入失敗');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPopup, startDate, endDate]);

  if (popups.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">尚未有任何彈窗，無法顯示成效報表。</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 md:p-6 flex flex-col md:flex-row gap-4 items-start md:items-end">
        <div className="flex-1 w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">選擇彈窗</label>
          <select
            value={selectedPopup}
            onChange={(e) => setSelectedPopup(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
          >
            {popupOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">快速範圍</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
          >
            <option value={7}>最近 7 天</option>
            <option value={30}>最近 30 天</option>
            <option value={90}>最近 90 天</option>
            <option value={0}>自訂</option>
          </select>
        </div>

        <div className="flex gap-2 w-full md:w-auto">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">開始</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setDays(0); setStartDate(e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">結束</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setDays(0); setEndDate(e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Eye} label="曝光次數" value={data.impressions} color="blue" />
            <KpiCard icon={MousePointerClick} label="點擊次數" value={data.clicks} color="green" />
            <KpiCard icon={XCircle} label="關閉次數" value={data.closes} color="gray" />
            <KpiCard icon={BarChart3} label="點擊率 CTR" value={data.clickThroughRate} color="red" />
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">轉換漏斗</h3>
            <div className="space-y-3">
              {CONVERSION_STEPS.map((step, index) => {
                const value = data.conversions[step.key as keyof typeof data.conversions] || 0;
                const prevValue = index === 0 ? data.clicks : data.conversions[CONVERSION_STEPS[index - 1].key as keyof typeof data.conversions] || 0;
                const rate = prevValue > 0 ? ((value / prevValue) * 100).toFixed(2) + '%' : '0.00%';
                const width = data.clicks > 0 ? Math.max(4, (value / data.clicks) * 100) : 0;
                return (
                  <div key={step.key} className="relative">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <step.icon className="w-4 h-4" />
                        <span>{step.label}</span>
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {value.toLocaleString()}
                        {index > 0 && prevValue > 0 && (
                          <span className="text-xs text-gray-500 ml-2">{rate}</span>
                        )}
                      </div>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-600 rounded-full transition-all duration-500"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">點擊 → 訂單</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.conversionRate.clickToOrder}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">曝光 → 訂單</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{data.conversionRate.impressionToOrder}</p>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

function KpiCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: 'blue' | 'green' | 'gray' | 'red';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    gray: 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

export default PopupAnalyticsReport;
